import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ConfirmationResult, RecaptchaVerifier } from 'firebase/auth';
import { getAuthOrThrow } from '../../lib/firebase';
import {
  completePhoneVerification,
  createPhoneRecaptchaVerifier,
  disposeRecaptchaVerifier,
  formatUnknownPhoneAuthError,
  normalizeIsraeliMobileToE164,
  phoneAuthErrorMessage,
  requestPhoneSms,
} from '../../lib/phoneAuth';

export type PhoneAuthVariant = 'login' | 'register';

interface Props {
  variant: PhoneAuthVariant;
  onSwitchVariant: () => void;
  onSuccess: () => void;
}

export function PhoneAuthForm({ variant, onSwitchVariant, onSuccess }: Props) {
  const auth = getAuthOrThrow();
  const recaptchaHostId = useId().replace(/:/g, '');
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationRef = useRef<ConfirmationResult | null>(null);

  const [phoneInput, setPhoneInput] = useState('');
  const [sentE164, setSentE164] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [phase, setPhase] = useState<'phone' | 'code'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      disposeRecaptchaVerifier(recaptchaVerifierRef.current);
      recaptchaVerifierRef.current = null;
      confirmationRef.current = null;
    };
  }, []);

  const handleSendCode = useCallback(async () => {
    setError(null);
    setBusy(true);
    disposeRecaptchaVerifier(recaptchaVerifierRef.current);
    recaptchaVerifierRef.current = null;
    try {
      const e164 = normalizeIsraeliMobileToE164(phoneInput);
      const host = recaptchaContainerRef.current;
      if (!host) throw new Error('recaptcha-host');
      const verifier = createPhoneRecaptchaVerifier(auth, host);
      recaptchaVerifierRef.current = verifier;
      const cr = await requestPhoneSms(auth, e164, verifier);
      disposeRecaptchaVerifier(recaptchaVerifierRef.current);
      recaptchaVerifierRef.current = null;
      confirmationRef.current = cr;
      setSentE164(e164);
      setPhase('code');
    } catch (e) {
      const msg =
        e && typeof e === 'object' && 'code' in e
          ? phoneAuthErrorMessage(String((e as { code: string }).code))
          : e instanceof Error && e.message === 'INVALID_IL_PHONE'
            ? phoneAuthErrorMessage('INVALID_IL_PHONE')
            : e instanceof Error && e.message === 'EMPTY_PHONE'
              ? phoneAuthErrorMessage('EMPTY_PHONE')
              : e instanceof Error && e.message === 'recaptcha-host'
                ? 'שגיאת תצוגה. רעננו את הדף.'
                : formatUnknownPhoneAuthError(e);
      setError(msg);
      disposeRecaptchaVerifier(recaptchaVerifierRef.current);
      recaptchaVerifierRef.current = null;
    } finally {
      setBusy(false);
    }
  }, [auth, phoneInput]);

  const handleVerifyCode = useCallback(async () => {
    const cr = confirmationRef.current;
    if (!cr) {
      setError('בקשו קוד חדש — הפגה ההזדהות.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await completePhoneVerification(auth, cr.verificationId, codeInput);
      confirmationRef.current = null;
      onSuccess();
    } catch (e) {
      setError(formatUnknownPhoneAuthError(e));
    } finally {
      setBusy(false);
    }
  }, [auth, codeInput, onSuccess]);

  const title = variant === 'login' ? 'התחברות' : 'הרשמה';
  const subtitle =
    variant === 'login'
      ? 'הזינו את מספר הנייד — נשלח לכם קוד SMS לאימות.'
      : 'נרשמים עם מספר נייד ישראלי. נשלח קוד SMS לאימות החשבון.';

  return (
    <div className="screen">
      <div className="screen-scroll" style={{ paddingTop: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>
          רגעים
        </div>
        <h1 className="h1" style={{ marginTop: 0 }}>
          {title}
        </h1>
        <p className="lede" style={{ marginTop: 0, marginBottom: 20 }}>
          {subtitle}
        </p>

        {phase === 'phone' ? (
          <>
            <label className="phone-auth-label" htmlFor={`phone-${recaptchaHostId}`}>
              מספר טלפון נייד
            </label>
            <input
              id={`phone-${recaptchaHostId}`}
              className="phone-auth-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="050-1234567"
              value={phoneInput}
              onChange={(ev) => setPhoneInput(ev.target.value)}
              disabled={busy}
            />
            <p className="phone-auth-hint" style={{ marginTop: 12, marginBottom: 8 }}>
              לפני שליחת הקוד, סמנו את תיבת האבטחה:
            </p>
            <div ref={recaptchaContainerRef} className="phone-recaptcha-host" />
            <button
              type="button"
              className="btn-primary"
              style={{ width: '100%', marginTop: 16 }}
              disabled={busy}
              onClick={() => void handleSendCode()}
            >
              שלחו לי קוד
            </button>
          </>
        ) : (
          <>
            <p className="lede" style={{ fontSize: 14, marginBottom: 12 }}>
              הזינו את הקוד שקיבלתם ב-SMS ל־{sentE164 ?? '…'}.
            </p>
            <label className="phone-auth-label" htmlFor={`code-${recaptchaHostId}`}>
              קוד אימות
            </label>
            <input
              id={`code-${recaptchaHostId}`}
              className="phone-auth-input phone-auth-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="______"
              maxLength={6}
              value={codeInput}
              onChange={(ev) => setCodeInput(ev.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={busy}
            />
            <button
              type="button"
              className="btn-primary"
              style={{ width: '100%', marginTop: 16 }}
              disabled={busy || codeInput.length < 6}
              onClick={() => void handleVerifyCode()}
            >
              אימות והמשך
            </button>
            <button
              type="button"
              className="btn-ghost"
              style={{ width: '100%', marginTop: 10 }}
              disabled={busy}
              onClick={() => {
                setPhase('phone');
                setCodeInput('');
                setSentE164(null);
                setError(null);
                confirmationRef.current = null;
                disposeRecaptchaVerifier(recaptchaVerifierRef.current);
                recaptchaVerifierRef.current = null;
              }}
            >
              חזרה לשינוי מספר
            </button>
          </>
        )}

        <div className="phone-auth-switch">
          {variant === 'login' ? (
            <>
              אין לכם חשבון?{' '}
              <button type="button" className="phone-auth-link" onClick={onSwitchVariant}>
                הרשמה
              </button>
            </>
          ) : (
            <>
              כבר רשומים?{' '}
              <button type="button" className="phone-auth-link" onClick={onSwitchVariant}>
                התחברות
              </button>
            </>
          )}
        </div>

        {variant === 'register' && phase === 'phone' && (
          <p className="phone-auth-legal" style={{ marginTop: 20 }}>
            בהמשך השימוש אתם מאשרים קבלת SMS לאימות ועדכונים חיוניים לשירות, בהתאם
            למדיניות Firebase ולספק הסלולר.
          </p>
        )}

        {error && <div className="phone-auth-error">{error}</div>}
      </div>
    </div>
  );
}
